import { Add as AddIcon, Edit as EditIcon } from '@mui/icons-material';
import { Alert, AlertTitle, Box, Card, CardContent, CardHeader, IconButton, Typography } from '@mui/material';
import { useCallback, useRef, useState } from 'react';
import {
  BooleanInput,
  Button,
  Datagrid,
  DeleteButton,
  Edit,
  FunctionField,
  ReferenceManyField,
  required,
  SelectInput,
  SimpleForm,
  TextField,
  TextInput,
  useInput,
  useRecordContext,
  useSaveContext,
  useTranslate,
  ValidationError,
  ValidationErrorMessage,
} from 'react-admin';
import { FieldValues, useWatch } from 'react-hook-form';
import { MountedFieldsProbe, MountedFieldsReader, partitionServerErrors } from 'components/ServerValidationErrors';
import { IconPicker } from '../../components/IconPicker';
import { CommandParameterDialog } from './CommandParameterDialog';

// Wrapper to make IconPicker compatible with React Admin
const IconPickerInput = (props: any) => {
  const { field } = useInput(props);
  return <IconPicker {...field} label={props.label} helperText={props.helperText} />;
};

export const CommandEdit = () => {
  return (
    <Edit mutationMode="pessimistic">
      <CommandEditForm />
    </Edit>
  );
};

/**
 * The form proper, one level below `<Edit>` so it can reach `useSaveContext()` and save by hand.
 *
 * Saving by hand buys exactly one thing: the chance to look at a 422 before react-admin sets it on
 * the form. `UpdateCommandRequest` validates several fields this form does not always render —
 * `min_version` and `max_version` have no input at all, and the whole `feedback_config.*` group is
 * mounted only while `feedback` is on, with `custom_topic`/`custom_message` mounted only while
 * `wait_type` is `topic_message`. `feedback_config.wait_type` is `required_with:feedback_config`,
 * so a row carrying a legacy or later-disabled `feedback_config` 422s on a field whose input is not
 * on screen.
 *
 * In `mutationMode="pessimistic"` that error would otherwise be set on an unregistered field name,
 * where nothing renders it and nothing ever clears it — see `components/ServerValidationErrors` for
 * why that wedges every later submit. Partitioning keeps such a message out of `formState.errors`
 * and puts it in the alert below, so the user can read it and try again.
 *
 * The payload is the form's values untouched: `save(values)` is what `useAugmentedForm` would have
 * called for us, so nothing about what is submitted changes.
 */
const CommandEditForm = () => {
  const { save } = useSaveContext();
  const translate = useTranslate();

  const [formErrors, setFormErrors] = useState<ValidationErrorMessage[]>([]);
  const mountedFields = useRef<MountedFieldsReader>(undefined);

  const handleSubmit = useCallback(
    async (values: FieldValues) => {
      /**
       * Unreachable while this component is rendered where it is. `useEditController` builds `save`
       * with an unconditional `useCallback` and `EditBase` publishes it through `usePickSaveContext`,
       * so anything under `<Edit>` sees a function on every render, loaded record or not. `save` is
       * undefined only outside a save context altogether — `SaveContext`'s default value is the empty
       * object (`ra-core/src/controller/saveContext/SaveContext.ts`) — and `CommandEditForm` is
       * module-private, rendered by `CommandEdit` alone, one level below its `<Edit>`.
       *
       * The branch is here because `SaveContextValue.save` is typed optional, not because it can
       * happen; that is also why it shows up as the uncovered line of this file, and why there is no
       * user feedback to give in it.
       */
      if (save == null) {
        return undefined;
      }

      setFormErrors([]);
      const result: unknown = await save(values);
      if (result == null) {
        return undefined;
      }

      const { fieldErrors, formErrors: unboundErrors } = partitionServerErrors(
        result,
        (field) => mountedFields.current?.().has(field) ?? false,
      );
      setFormErrors(unboundErrors);

      return fieldErrors;
    },
    [save],
  );

  return (
    <SimpleForm onSubmit={handleSubmit}>
      <MountedFieldsProbe mountedFields={mountedFields} />
      {formErrors.length > 0 && (
        <Alert severity="error" sx={{ width: '100%' }}>
          <AlertTitle>{translate('resources.commands.edit.save_failed')}</AlertTitle>
          {formErrors.map((error, index) => (
            // eslint-disable-next-line react/no-array-index-key -- two fields can report the same message
            <div key={index}>
              <ValidationError error={error} />
            </div>
          ))}
        </Alert>
      )}
      <TextInput source="name" validate={[required()]} fullWidth />
      <TextInput source="display_name" validate={[required()]} fullWidth label="Display Name" />
      <TextInput source="description" validate={[required()]} multiline rows={3} fullWidth />
      <SelectInput
        source="category"
        choices={[
          { id: 'system', name: 'System' },
          { id: 'application', name: 'Application' },
          { id: 'network', name: 'Network' },
          { id: 'diagnostics', name: 'Diagnostics' },
        ]}
        validate={[required()]}
      />
      <SelectInput
        source="danger_level"
        choices={[
          { id: 'low', name: 'Low' },
          { id: 'medium', name: 'Medium' },
          { id: 'high', name: 'High' },
          { id: 'critical', name: 'Critical' },
        ]}
        validate={[required()]}
      />
      <IconPickerInput source="icon" label="Icon" helperText="Select an icon for this command" />
      <TextInput
        source="mqtt_topic_pattern"
        validate={[required()]}
        fullWidth
        label="MQTT Topic Pattern"
        helperText="e.g., CMD/{hostname}/{command}"
      />
      <BooleanInput source="is_active" />
      <BooleanInput source="confirmation_required" label="Confirmation Required" />

      {/* Feedback Configuration Section */}
      <Box sx={{ mt: 3, mb: 2 }}>
        <Typography variant="h6" gutterBottom>
          Feedback Configuration
        </Typography>
        <BooleanInput
          source="feedback"
          label="Enable Real-time Feedback"
          helperText="Monitor command execution and show real-time output"
        />
      </Box>

      <FeedbackConfigFields />

      <ParametersReferenceManyField />
    </SimpleForm>
  );
};

// Separate component to use useWatch for conditional rendering
const FeedbackConfigFields = () => {
  const feedbackEnabled = useWatch({ name: 'feedback' });
  const waitType = useWatch({ name: 'feedback_config.wait_type' });

  if (!feedbackEnabled) return null;

  return (
    <Box sx={{ pl: 2, borderLeft: '3px solid', borderColor: 'primary.main', mb: 2 }}>
      <SelectInput
        source="feedback_config.wait_type"
        label="Wait For"
        choices={[
          {
            id: 'done_message',
            name: 'Done Message on CMD_ACK/{hostname}',
          },
          {
            id: 'topic_message',
            name: 'Custom Topic/Message',
          },
        ]}
        validate={feedbackEnabled ? [required()] : []}
        helperText="What should the system wait for to confirm command completion?"
      />

      {waitType === 'topic_message' && (
        <>
          <TextInput
            source="feedback_config.custom_topic"
            label="Custom Topic Pattern"
            fullWidth
            helperText="e.g., UP/{hostname} or STATUS/{hostname}"
            validate={waitType === 'topic_message' ? [required()] : []}
          />
          <TextInput
            source="feedback_config.custom_message"
            label="Expected Message/Payload"
            fullWidth
            multiline
            rows={2}
            helperText='JSON payload to wait for, e.g., {"status":"ready"} or use {command} placeholder'
          />
        </>
      )}

      <BooleanInput
        source="feedback_config.show_terminal"
        label="Show Terminal Output"
        helperText="Display real-time command output in a terminal window"
      />
    </Box>
  );
};

const ParametersReferenceManyField = () => {
  const record = useRecordContext();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editParameter, setEditParameter] = useState<any>(null);
  const [dialogMode, setDialogMode] = useState<'create' | 'edit'>('create');

  if (!record?.id) {
    return (
      <Box sx={{ p: 2, backgroundColor: 'grey.100', borderRadius: 1 }}>Save the command first to add parameters.</Box>
    );
  }

  const handleCreate = () => {
    setDialogMode('create');
    setEditParameter(null);
    setDialogOpen(true);
  };

  const handleEdit = (parameter: any) => {
    setDialogMode('edit');
    setEditParameter(parameter);
    setDialogOpen(true);
  };

  const handleDialogClose = () => {
    setDialogOpen(false);
    setEditParameter(null);
  };

  return (
    <>
      <ReferenceManyField
        reference={`commands/${record?.id}/parameters`}
        target="command_id"
        label="Parameters"
        fullWidth
      >
        <Card variant="outlined" sx={{ mt: 2, p: 2, width: '100%' }}>
          <CardHeader sx={{ p: 0, mb: 2 }} title={<Typography variant="h6">Parameters</Typography>} />
          <CardContent>
            <Box sx={{ mb: 2 }}>
              <Button onClick={handleCreate} variant="contained" startIcon={<AddIcon />}>
                Add Parameter
              </Button>
            </Box>
            <Datagrid bulkActionButtons={false} rowClick={false} empty={<Typography>No parameters defined</Typography>}>
              <TextField source="name" />
              <TextField source="display_name" />
              <TextField source="data_type" />
              <TextField source="ui_component" />
              <TextField source="is_required" label="Required" />
              <TextField source="order" />
              <FunctionField
                label="Actions"
                render={(param: any) => (
                  <IconButton
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleEdit(param);
                    }}
                    sx={{ mr: 1 }}
                  >
                    <EditIcon fontSize="small" />
                  </IconButton>
                )}
              />
              <DeleteButton
                mutationMode="pessimistic"
                confirmTitle="Delete Parameter"
                confirmContent="Are you sure you want to delete this parameter?"
                redirect={false}
              />
            </Datagrid>
          </CardContent>
        </Card>
      </ReferenceManyField>

      <CommandParameterDialog
        open={dialogOpen}
        onClose={handleDialogClose}
        commandId={record.id}
        parameter={editParameter}
        mode={dialogMode}
      />
    </>
  );
};
